import { MINUTES_PER_DAY } from '../model/constants';
import { BlockStatus } from '../model/enums';
import { isLive, type LiveTimeBlock, type TimeBlockRecord } from '../model/types';
import { epochDayOf } from '../time/date';

/**
 * Statistics operate on `TimeBlockRecord` envelopes (the stored record type) rather than a
 * separate view type. Only live, confirmed blocks count; drafts never do.
 */

export interface DateRange {
  /** First personal date, inclusive. */
  readonly from: string;
  /** Last personal date, inclusive. */
  readonly to: string;
}

/** A stretch of time attributed to exactly one block; `startAbs`/`endAbs` are minutes since 1970-01-01 00:00 wall time. */
export interface Segment {
  readonly startAbs: number;
  readonly endAbs: number;
  readonly projectId: string;
  readonly blockId: string;
}

export function absoluteMin(personalDate: string, wallMin: number): number {
  return epochDayOf(personalDate) * MINUTES_PER_DAY + wallMin;
}

export function confirmedBlocksIn(blocks: readonly TimeBlockRecord[], range?: DateRange): LiveTimeBlock[] {
  return blocks
    .filter(isLive)
    .filter(
      (block) =>
        block.data.status === BlockStatus.Confirmed &&
        (range === undefined ||
          (block.data.personalDate >= range.from && block.data.personalDate <= range.to)),
    );
}

interface Interval {
  readonly start: number;
  readonly end: number;
  readonly block: LiveTimeBlock;
}

/** Count attribution (data dictionary §3): the greater `countRank` wins, ties go to the greater id. */
function outranks(a: LiveTimeBlock, b: LiveTimeBlock): boolean {
  const rankA = a.data.countRank ?? 0;
  const rankB = b.data.countRank ?? 0;
  if (rankA !== rankB) return rankA > rankB;
  return a.id > b.id;
}

/**
 * Union of the confirmed blocks as non-overlapping segments. Minutes covered by several blocks
 * are attributed to the block with the greater `(countRank, id)` (absent `countRank` counts as
 * 0), so they are never counted twice, and the attribution does not move when an unrelated edit
 * or a sync rewrites a block. Segments are ordered by time.
 */
export function resolveSegments(blocks: readonly TimeBlockRecord[], range?: DateRange): Segment[] {
  const intervals: Interval[] = confirmedBlocksIn(blocks, range)
    .map((block) => ({
      start: absoluteMin(block.data.personalDate, block.data.startMin),
      end: absoluteMin(block.data.personalDate, block.data.endMin),
      block,
    }))
    .sort((a, b) => a.start - b.start);
  const boundaries = [...new Set(intervals.flatMap((interval) => [interval.start, interval.end]))].sort(
    (a, b) => a - b,
  );

  const segments: Segment[] = [];
  let active: Interval[] = [];
  let next = 0;
  for (let i = 0; i + 1 < boundaries.length; i += 1) {
    const from = boundaries[i];
    const to = boundaries[i + 1];
    if (from === undefined || to === undefined) continue;
    while (next < intervals.length && (intervals[next]?.start ?? Infinity) <= from) {
      const added = intervals[next];
      if (added !== undefined) active.push(added);
      next += 1;
    }
    active = active.filter((interval) => interval.end > from);
    let winner: Interval | undefined;
    for (const interval of active) {
      if (winner === undefined || outranks(interval.block, winner.block)) winner = interval;
    }
    if (winner !== undefined) {
      segments.push({
        startAbs: from,
        endAbs: to,
        projectId: winner.block.data.projectId,
        blockId: winner.block.id,
      });
    }
  }
  return segments;
}
